import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PaginationTable from '../../pagination/Pagination4';

const CBReport_Fps = ({ month, year, dist_code, dist_name, afso_code, afso_name }) => {
    const [reportData, setReportData] = useState([]);
    const [schemeCommList, setSchemeCommList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noData, setNoData] = useState(false);
    const [tabHeading, setTabHeading] = useState(null);
    const [serverError, setServerError] = useState(false);
    const [consolidatedTableExists, setConsolidatedTableExists] = useState(true);
    const [dataTime, setDataTime] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchQuery, setSearchQuery] = useState('');

    const getMonthName = (month) => {
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return monthNames[parseInt(month) - 1];
    };

    const groupSchemes = (schemeList) => {
        const schemeMap = new Map();

        schemeList.forEach(item => {
            if (!schemeMap.has(item.scheme_id)) {
                schemeMap.set(item.scheme_id, {
                    scheme_id: item.scheme_id,
                    scheme_name: item.scheme_name_en,
                    commodities: []
                });
            }
            schemeMap.get(item.scheme_id).commodities.push({
                comm_id: item.comm_id,
                comm_name: item.comm_name_en || item.comm_name
            });
        });

        return Array.from(schemeMap.values());
    };

    const handleDownload = () => {
        const table = document.getElementById('fpsReportTable');
        if (!table) return;

        const tableClone = table.cloneNode(true);

        tableClone.querySelectorAll('td, th').forEach(cell => {
            cell.setAttribute('style', 'mso-number-format:"\\@"; text-align: center;');
        });
        tableClone.querySelectorAll('.tableheader th').forEach(th => {
            th.setAttribute('style', 'background-color: #de7d22; color: white; font-weight: bold; text-align: center; mso-number-format:"\\@";');
        });

        const totalRows = tableClone.querySelectorAll('.tableheader');
        if (totalRows.length > 0) {
            const totalRow = totalRows[totalRows.length - 1];
            totalRow.querySelectorAll('td, th').forEach(cell => {
                cell.setAttribute('style', 'background-color: #de7d22; color: white; font-weight: bold; text-align: center; mso-number-format:"\\@";');
            });
        }

        if (totalRows.length > 1) {
            const dataTimeRow = totalRows[totalRows.length - 2];
            if (dataTimeRow.textContent.includes('Data Time')) {
                dataTimeRow.querySelectorAll('th').forEach(th => {
                    th.setAttribute('style', 'background-color: #de7d22; color: white; font-weight: bold; text-align: right; mso-number-format:"\\@";');
                });
            }
        }

        const dataType = 'application/vnd.ms-excel';
        const tableHTML = tableClone.outerHTML;

        const downloadLink = document.createElement('a');
        document.body.appendChild(downloadLink);

        downloadLink.href = 'data:' + dataType + ', ' + encodeURIComponent(tableHTML);
        downloadLink.download = `Concealed and Reconciled Stock for Allotment in ${dist_name} ${afso_name} - ${getMonthName(month)} ${year}.xls`;
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                setNoData(false);
                setServerError(false);

                const response = await fetch(`${process.env.REACT_APP_URL_SALE}/api/CBReport/getFpsCBData`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ month, year, dist_code, afso_code }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const response_data = await response.json();

                setTabHeading(`Concealed and Reconciled Stock for Allotment in${dist_name} ${afso_name} - ${getMonthName(month)} ${year}`);

                if (response_data && response_data.length > 0) {
                    setReportData(response_data);
                    setNoData(false);

                    if (response_data[0] && response_data[0].update_time) {
                        setDataTime(response_data[0].update_time.substring(0, 16));
                    }

                    const hasConsolidatedData = response_data.some(fps =>
                        fps.consolidatedMap && Object.values(fps.consolidatedMap).some(val => val !== null && val !== 0)
                    );
                    setConsolidatedTableExists(hasConsolidatedData);

                    if (response_data[0] && response_data[0].schemeCommList && response_data[0].schemeCommList.length > 0) {
                        const grouped = groupSchemes(response_data[0].schemeCommList);
                        setSchemeCommList(grouped);
                    }
                } else {
                    setReportData([]);
                    setSchemeCommList([]);
                    setNoData(true);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setServerError(true);
                setError('Internal Exception');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [month, year, dist_code, afso_code, afso_name]);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const filteredData = reportData.filter(item =>
        (item?.fps_id || "").toLowerCase().includes(searchQuery.toLowerCase())
    );

    const paginatedData = rowsPerPage >= filteredData.length
        ? filteredData
        : filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (value) => {
        if (value === 'all') {
            setRowsPerPage(filteredData.length);
        } else {
            setRowsPerPage(parseInt(value, 10));
        }
        setPage(0);
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: '7px' }}>
                <CircularProgress />
            </div>
        );
    }

    if (serverError) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '30px',
                color: 'red',
                backgroundColor: '#ffeeee',
                margin: '20px',
                borderRadius: '5px'
            }}>
                <p>{error || 'Internal Exception'}</p>
            </div>
        );
    }

    if (noData || reportData.length === 0) {
        return (
            <div
                align="center"
                style={{
                    margin: '20px',
                    fontWeight: 'bold',
                    color: 'brown',
                    textDecoration: 'underline',
                    fontSize: '18px'
                }}
            >
                <p>No Data Found</p>
            </div>
        );
    }

    let totalCommodities = 0;
    schemeCommList.forEach(scheme => {
        totalCommodities += scheme.commodities.length;
    });

    const totals = {
        unconsolidated: {},
        consolidated: {}
    };

    schemeCommList.forEach(scheme => {
        scheme.commodities.forEach(comm => {
            const key = `${scheme.scheme_id}_${comm.comm_id}`;
            totals.unconsolidated[key] = 0;
            totals.consolidated[key] = 0;
        });
    });

    reportData.forEach(fps => {
        schemeCommList.forEach(scheme => {
            scheme.commodities.forEach(comm => {
                const key = `${scheme.scheme_id}_${comm.comm_id}`;

                const unconsValue = fps.unconsolidatedMap?.[key];
                if (unconsValue !== null && unconsValue !== undefined) {
                    totals.unconsolidated[key] += unconsValue;
                }

                const consValue = fps.consolidatedMap?.[key];
                if (consValue !== null && consValue !== undefined) {
                    totals.consolidated[key] += consValue;
                }
            });
        });
    });

    return (
        <>
            <br />
            <br />

            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '90%',
                margin: '0 auto',
                marginBottom: '20px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flex: 1
                }}>
                    <PaginationTable
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>

                <div style={{
                    position: 'relative',
                    marginLeft: 'auto'
                }}>
                    <input
                        type="text"
                        placeholder="Search FPS ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            padding: '8px',
                            width: '250px',
                            paddingRight: '25px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                    />
                    {searchQuery && (
                        <span
                            onClick={() => setSearchQuery('')}
                            style={{
                                position: 'absolute',
                                right: 6,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                userSelect: 'none',
                            }}
                            title="Clear Search"
                        >
                            ✖
                        </span>
                    )}
                </div>
            </div>

            {searchQuery && (
                <div style={{ width: '90%', margin: '0 auto 10px auto', textAlign: 'center', color: '#666' }}>
                    Found {filteredData.length} results for "{searchQuery}"
                </div>
            )}

            <div style={{
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: '700px',
                maxWidth: '90%',
                margin: '0 auto'
            }}>
                <table
                    id="fpsReportTable"
                    align="center"
                    border="1"
                    cellPadding="6"
                    style={{
                        minWidth: '100%',
                        width: 'max-content',
                        borderCollapse: 'collapse'
                    }}
                >
                    <thead style={{
                        position: 'sticky',
                        top: 0,
                        background: '#fff',
                        zIndex: 10
                    }}>
                        {tabHeading && (
                            <tr className="tableheader">
                                <th colSpan={2 + (totalCommodities * 2)}>
                                    {tabHeading}
                                </th>
                            </tr>
                        )}
                        <tr className="tableheader">
                            <th rowSpan="3">S.No</th>
                            <th rowSpan="3">FPS ID</th>
                            <th colSpan={totalCommodities}>Concealed Stock</th>
                            <th colSpan={totalCommodities}>Reconciled Stock</th>
                        </tr>
                        <tr className="tableheader">
                            {schemeCommList.map((scheme, index) => (
                                <th key={`uncons-scheme-${index}`} colSpan={scheme.commodities.length}>
                                    {scheme.scheme_name}
                                </th>
                            ))}
                            {schemeCommList.map((scheme, index) => (
                                <th key={`cons-scheme-${index}`} colSpan={scheme.commodities.length}>
                                    {scheme.scheme_name}
                                </th>
                            ))}
                        </tr>
                        <tr className="tableheader">
                            {schemeCommList.map(scheme =>
                                scheme.commodities.map((comm, idx) => (
                                    <th key={`uncons-comm-${scheme.scheme_id}-${idx}`}>
                                        {comm.comm_name}
                                    </th>
                                ))
                            )}
                            {schemeCommList.map(scheme =>
                                scheme.commodities.map((comm, idx) => (
                                    <th key={`cons-comm-${scheme.scheme_id}-${idx}`}>
                                        {comm.comm_name}
                                    </th>
                                ))
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((fps, index) => (
                                <tr key={fps.fps_id}>
                                    <td style={{ textAlign: 'center' }}>
                                        {index + 1 + page * rowsPerPage}
                                    </td>
                                    <td style={{ textAlign: 'left', paddingLeft: '10px' }}>
                                        {fps.fps_id}
                                    </td>

                                    {schemeCommList.map(scheme =>
                                        scheme.commodities.map((comm, idx) => {
                                            const key = `${scheme.scheme_id}_${comm.comm_id}`;
                                            const value = fps.unconsolidatedMap ? fps.unconsolidatedMap[key] : null;
                                            return (
                                                <td key={`uncons-${fps.fps_id}-${scheme.scheme_id}-${idx}`}
                                                    style={{ textAlign: 'right' }}>
                                                    {value === null || value === undefined ? 'NA' : value.toFixed(3)}
                                                </td>
                                            );
                                        })
                                    )}

                                    {schemeCommList.map(scheme =>
                                        scheme.commodities.map((comm, idx) => {
                                            const key = `${scheme.scheme_id}_${comm.comm_id}`;
                                            const value = fps.consolidatedMap ? fps.consolidatedMap[key] : null;
                                            return (
                                                <td key={`cons-${fps.fps_id}-${scheme.scheme_id}-${idx}`}
                                                    style={{ textAlign: 'right' }}>
                                                    {!consolidatedTableExists ? '-' : (value === null || value === undefined ? 'NA' : value.toFixed(3))}
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={2 + (totalCommodities * 2)} style={{ textAlign: 'center', padding: '20px' }}>
                                    No results found for "{searchQuery}"
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="tableheader" style={{ fontWeight: 'bold' }}>
                            <td colSpan={2} style={{ textAlign: 'center' }}>Total</td>

                            {schemeCommList.map(scheme =>
                                scheme.commodities.map((comm, idx) => {
                                    const key = `${scheme.scheme_id}_${comm.comm_id}`;
                                    const value = totals.unconsolidated[key];
                                    return (
                                        <td key={`total-uncons-${scheme.scheme_id}-${idx}`}
                                            style={{ textAlign: 'center' }}>
                                            {value === 0 ? '0.000' : value.toFixed(3)}
                                        </td>
                                    );
                                })
                            )}

                            {schemeCommList.map(scheme =>
                                scheme.commodities.map((comm, idx) => {
                                    const key = `${scheme.scheme_id}_${comm.comm_id}`;
                                    const value = totals.consolidated[key];
                                    return (
                                        <td key={`total-cons-${scheme.scheme_id}-${idx}`}
                                            style={{ textAlign: 'center' }}>
                                            {!consolidatedTableExists ? '-' : (value === 0 ? '0.000' : value.toFixed(3))}
                                        </td>
                                    );
                                })
                            )}
                        </tr>

                        {/* {dataTime && (
                            <tr className="tableheader">
                                <th colSpan={2 + (totalCommodities * 2)} style={{ textAlign: 'right' }}>
                                    Data Time: {dataTime}
                                </th>
                            </tr>
                        )} */}
                    </tfoot>
                </table>
            </div>

            <div align="center" style={{ marginTop: '20px' }}>
                <button type="button" className="button button2" onClick={handleDownload}>
                    Data to Excel
                </button>
            </div>

            <br />
            <br />
            <br />
            <br />
            <br></br>
        </>
    );
};

export default CBReport_Fps;